<template>
	<v-autocomplete
		v-bind="$attrs"
		:model-value="selectedIndustryIds"
		@update:model-value="updateSelectedIndustries"
		:items="items"
		item-title="name"
		item-value="id"
		multiple
		chips
		closable-chips
		clearable
		:loading="store.loading"
		no-data-text="No matching industry found.">
		<!-- Bold the trailing (Required)/(Optional) tag on the label (LSP-16777).
		     Label arrives via $attrs; render through FieldLabel unless a caller supplies #label. -->
		<template v-if="$attrs.label && !$slots.label" #label>
			<FieldLabel :label="String($attrs.label)" />
		</template>
		<template #subheader="{ props }">
			<v-list-subheader class="font-weight-bold bg-grey-lighten-2">{{ props.title }}</v-list-subheader>
		</template>
		<template #chip="{ item, props: chipProps }">
			<v-chip v-if="isIndustrySelection(item.raw)" closable close-icon="icon-close-medium" v-bind="chipProps">
				<span class="text-truncate">{{ item.title }}</span>
			</v-chip>
		</template>
	</v-autocomplete>
</template>

<script setup lang="ts">
	import { computed, onMounted } from "vue";
	import { useIndustriesStore } from "../../stores/industries";
	import type { Industry } from "../../types/organization/Industry";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	defineOptions({ inheritAttrs: false });

	const model = defineModel<string[] | null>();
	const store = useIndustriesStore();
	const isResolvingIndustries = computed(() => store.loading || (!store.loaded && !store.loadFailed));
	const selectedIndustryIds = computed<string[]>(() => (isResolvingIndustries.value ? [] : (model.value ?? [])));

	onMounted(() => {
		store.load();
	});

	function updateSelectedIndustries(value: string[] | null): void {
		if (isResolvingIndustries.value) {
			return;
		}
		model.value = value;
	}

	function isIndustrySelection(value: unknown): value is Industry {
		return typeof value === "object" && value !== null && "id" in value && "name" in value;
	}

	type PickerItem = Industry | { type: "subheader"; title: string } | { type: "divider" };

	const items = computed<PickerItem[]>(() => {
		const grouped = new Map<string, Industry[]>();
		for (const industry of store.industries ?? []) {
			const bucket = grouped.get(industry.category) ?? [];
			bucket.push(industry);
			grouped.set(industry.category, bucket);
		}

		const result: PickerItem[] = [];
		let first = true;
		for (const [category, list] of grouped) {
			if (!first) {
				result.push({ type: "divider" });
			}
			first = false;
			result.push({ type: "subheader", title: category });
			result.push(...list);
		}
		return result;
	});
</script>
